import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import Loader from "../components/Loader";
import { container, item } from "../lib/motion";
import { Flame, Zap, Trophy, Check, Plus, Trash2, Footprints, Calendar, TrendingUp, Shield, BookOpen, Sparkles } from "lucide-react";

const BADGE_ICONS = { footprints: Footprints, flame: Flame, calendar: Calendar, "trending-up": TrendingUp, shield: Shield, "book-open": BookOpen };
const MOODS = ["calm", "grateful", "focused", "tired", "anxious", "motivated"];

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [mood, setMood] = useState("calm");
  const [newHabit, setNewHabit] = useState("");

  const load = async () => {
    const { data } = await api.get("/me/dashboard");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const checkin = async () => {
    try {
      await api.post("/checkin", { mood, note: "" });
      toast.success("Checked in · +10 XP");
      await load(); refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Already checked in"); }
  };
  const addHabit = async (e) => {
    e.preventDefault();
    if (!newHabit.trim()) return;
    await api.post("/habits", { name: newHabit });
    setNewHabit(""); load();
  };
  const toggleHabit = async (id) => { await api.post(`/habits/${id}/toggle`); load(); refreshUser(); };
  const delHabit = async (id) => { await api.delete(`/habits/${id}`); load(); };

  if (!data) return <Layout><Loader label="Entering your sanctuary" /></Layout>;

  return (
    <Layout glow="split">
      <motion.div className="max-w-6xl mx-auto px-6 md:px-10 py-16" initial="hidden" animate="show" variants={container}>
        <motion.div variants={item}>
          <span className="eyebrow">Your Sanctuary</span>
          <h1 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Welcome back, <span className="font-semibold">{user?.name?.split(" ")[0] || "friend"}</span></h1>
          <p className="text-zinc-500 mt-3 font-light text-lg">A quiet space to watch yourself become. Keep showing up.</p>
        </motion.div>

        {/* Hero stat row */}
        <motion.div variants={item} className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          <Stat icon={Flame} label="Day Streak" value={data.streak} testid="stat-streak" suffix="days" />
          <Stat icon={Zap} label="Total XP" value={data.xp} testid="stat-xp" />
          <Stat icon={Trophy} label="Level" value={data.level} testid="stat-level" />
          <Stat icon={Sparkles} label="Reflections" value={data.journal_count} testid="stat-journal" />
        </motion.div>

        {/* Level progress + streak rings */}
        <motion.div variants={item} className="glass-card p-8 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex-1 min-w-[260px]">
              <div className="flex justify-between text-sm mb-3">
                <span className="text-zinc-300 font-medium">Level {data.level}</span>
                <span className="text-zinc-500">{data.xp_in_level}/100 XP to level {data.level + 1}</span>
              </div>
              <div className="h-2.5 bg-black/50 rounded-full overflow-hidden">
                <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#2680eb,#4da6ff)", boxShadow: "0 0 16px rgba(77,166,255,0.6)" }}
                  initial={{ width: 0 }} animate={{ width: `${data.xp_in_level}%` }} transition={{ duration: 1, ease: [0.22,1,0.36,1] }} />
              </div>
            </div>
            <div className="flex items-center gap-2.5" data-testid="streak-rings">
              {[...Array(7)].map((_, i) => {
                const filled = i < Math.min(data.streak, 7);
                return <div key={i} className="w-4 h-4 rounded-full transition-all"
                  style={filled ? { background: "#4da6ff", boxShadow: "0 0 12px rgba(77,166,255,0.7)" } : { background: "transparent", border: "1px solid rgba(255,255,255,0.12)" }} />;
              })}
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Check-in */}
          <motion.div variants={item} className="glass-card p-8 lg:col-span-1">
            <h3 className="font-head text-xl font-medium mb-1.5">Daily Check-in</h3>
            <p className="text-zinc-500 text-sm mb-6 font-light">How are you arriving today?</p>
            {data.checked_in_today ? (
              <div className="text-center py-8" data-testid="checkin-done">
                <div className="w-14 h-14 rounded-full glass flex items-center justify-center mx-auto mb-4 glow-blue"><Check className="w-7 h-7 text-[#4da6ff]" /></div>
                <p className="text-zinc-300 text-sm font-light">You've arrived today.<br/>Streak secured.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-6">
                  {MOODS.map((m) => (
                    <button key={m} onClick={() => setMood(m)} data-testid={`mood-${m}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs capitalize transition-all ${mood === m ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>{m}</button>
                  ))}
                </div>
                <button onClick={checkin} data-testid="growth-tracker-checkin-button" className="w-full btn-primary font-medium py-3.5">Check in · +10 XP</button>
              </>
            )}
          </motion.div>

          {/* Habits */}
          <motion.div variants={item} className="glass-card p-8 lg:col-span-2">
            <h3 className="font-head text-xl font-medium mb-1.5">Rituals</h3>
            <p className="text-zinc-500 text-sm mb-6 font-light">Small acts, repeated. +5 XP each.</p>
            <form onSubmit={addHabit} className="flex gap-2.5 mb-5">
              <input value={newHabit} onChange={(e) => setNewHabit(e.target.value)} placeholder="Add a ritual (e.g. Read 10 pages)" data-testid="habit-input"
                className="flex-1 field px-4 py-3 text-sm" />
              <button type="submit" data-testid="habit-add" className="btn-accent px-4 flex items-center"><Plus className="w-5 h-5" /></button>
            </form>
            <div className="space-y-2.5">
              {data.habits.length === 0 && <p className="text-zinc-600 text-sm text-center py-8 font-light">No rituals yet. Begin with one.</p>}
              {data.habits.map((h) => (
                <div key={h.id} className="flex items-center gap-3.5 bg-black/30 rounded-2xl px-4 py-3.5 border border-white/5" data-testid={`habit-${h.id}`}>
                  <button onClick={() => toggleHabit(h.id)} data-testid={`habit-toggle-${h.id}`}
                    className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${h.done_today ? "bg-[#4da6ff] border-[#4da6ff] glow-blue" : "border-zinc-600 hover:border-[#4da6ff]"}`}>
                    {h.done_today && <Check className="w-4 h-4 text-white" />}
                  </button>
                  <span className={`flex-1 text-sm ${h.done_today ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{h.name}</span>
                  <span className="text-xs text-zinc-600">×{h.completions}</span>
                  <button onClick={() => delHabit(h.id)} data-testid={`habit-delete-${h.id}`} className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Achievements */}
        <motion.div variants={item} className="mt-14">
          <h3 className="font-head text-2xl font-light mb-7 tracking-tight">Milestones</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {data.all_badges.map((b) => {
              const earned = data.earned_badges.includes(b.id);
              const Icon = BADGE_ICONS[b.icon] || Trophy;
              return (
                <div key={b.id} className={`glass-card p-6 text-center transition-all ${earned ? "" : "opacity-40"}`} data-testid={`badge-${b.id}`}>
                  <div className={`w-12 h-12 rounded-2xl mx-auto flex items-center justify-center mb-4 ${earned ? "glass glow-blue" : "bg-white/5"}`}>
                    <Icon className={`w-6 h-6 ${earned ? "text-[#4da6ff]" : "text-zinc-600"}`} strokeWidth={1.5} />
                  </div>
                  <p className="text-sm font-medium">{b.name}</p>
                  <p className="text-xs text-zinc-600 mt-1 font-light leading-snug">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value, testid, suffix }) {
  return (
    <div className="glass-card p-7" data-testid={testid}>
      <Icon className="w-6 h-6 text-[#4da6ff] mb-5" strokeWidth={1.5} />
      <div className="font-head text-4xl font-light">{value}<span className="text-base text-zinc-600 ml-1.5 font-light">{suffix}</span></div>
      <div className="text-zinc-500 text-sm mt-1.5 font-light">{label}</div>
    </div>
  );
}
